/**
 * Pure session & payment rules. The backend owns delivery, correction,
 * consumption and the patient account (unpaid | partial | paid are its
 * projections); these only shape presentation and the reception boundary
 * (clinical fields never reach a payment surface).
 */

import type { BookingView } from '@/core/clinic/bookingsApi';
import {
  OWING_STATUSES,
  PAYMENT_METHODS,
  type DeliveredSessionView,
  type PaymentFactsStatus,
  type PaymentMethod,
  type PaymentStatus,
} from '@/core/clinic/sessionsApi';

/** Only a live, undelivered booking can be recorded — cancelled/no-show never. */
export const canDeliver = (b: Pick<BookingView, 'status' | 'delivered'>): boolean =>
  b.status === 'booked' && !b.delivered;

/**
 * ZOY-546/551: the start affordance, resolved from the booking row alone.
 * A Guest is bookable but may not begin a clinical session — the flag is on
 * the row precisely so no per-booking Patient request is ever needed.
 */
export const canStart = (
  b: Pick<BookingView, 'executionStatus'> & { patient: Pick<BookingView['patient'], 'registered'> },
): boolean => b.executionStatus === 'scheduled' && b.patient.registered;

/**
 * ZOY-551 (Product decision): the clinical record is written INSIDE a started
 * session. The frontend path is scheduled → Start → in_progress → Complete or
 * Incomplete, with no way into the delivery form before the start act. The
 * backend stays permissive on purpose; this is the one coherent client path.
 */
export const canRecordDelivery = (b: Pick<BookingView, 'executionStatus'>): boolean =>
  b.executionStatus === 'in_progress';

/**
 * Product 2026-10-01: a completed session always carries writing — at least
 * one of the two clinical fields. Two sessions keep theirs elsewhere and are
 * exempt: a group participation (the occurrence's shared Group Report) and an
 * assessment-workflow session (its own signed assessment).
 */
export const reportRequired = (
  b: Pick<BookingView, 'groupSessionId'>,
  needsAssessment: boolean,
): boolean =>
  !(typeof b.groupSessionId === 'string' && b.groupSessionId !== '') && !needsAssessment;

export const reportWritten = (notes: string, outcome: string): boolean =>
  notes.trim() !== '' || outcome.trim() !== '';

/** ZOY-547: only a STARTED, not-yet-completed session can close incomplete. */
export const canCloseIncomplete = (b: Pick<BookingView, 'executionStatus'>): boolean =>
  b.executionStatus === 'in_progress';

export interface SessionChip {
  /** i18n key to render. */
  key: string;
  kind: 'ok' | 'err' | 'warn' | 'info' | 'quiet';
}

/**
 * ZOY-551: the ONE list chip, from the server-derived executionStatus —
 * never recomputed from status + delivered. `incomplete` reads quiet by
 * product decision (visible, not alarming).
 */
export function sessionChip(
  b: Pick<BookingView, 'executionStatus' | 'endsAt'>,
  now: Date,
  isNext: boolean,
): SessionChip {
  switch (b.executionStatus) {
    case 'completed':
      return { key: 'bk.delivered', kind: 'ok' };
    case 'in_progress':
      return { key: 'bk.exec.in_progress', kind: 'info' };
    case 'cancelled':
      return { key: 'bk.status.cancelled', kind: 'quiet' };
    case 'no_show':
      return { key: 'bk.status.no_show', kind: 'err' };
    case 'incomplete':
      return { key: 'bk.status.incomplete', kind: 'quiet' };
    case 'scheduled':
      if (isNext) return { key: 'bk.next', kind: 'info' };
      if (Date.parse(b.endsAt) <= now.getTime())
        return { key: 'ss.chip.not_recorded', kind: 'warn' };
      return { key: 'bk.status.booked', kind: 'ok' };
  }
}

/** Mirrors validatePaymentFacts: paid needs a method; unpaid carries none. */
export function paymentValid(status: PaymentFactsStatus, method: PaymentMethod | null): boolean {
  if (status === 'paid')
    return method !== null && (PAYMENT_METHODS as readonly string[]).includes(method);
  return method === null;
}

/**
 * Package context line: counts derive from effective sessions; after this
 * save = delivered + 1. `beyondPlan` = the plan is already consumed, so this
 * delivery is allowed and priced from the clinic catalogue (Product
 * 2026-09-06) — information, never a block.
 */
export function afterSave(
  delivered: number,
  planned: number,
): { delivered: number; planned: number; after: number; beyondPlan: boolean } {
  return { delivered, planned, after: delivered + 1, beyondPlan: delivered >= planned };
}

/** Money still owed on this session: the account has not (fully) settled it. */
export const isOwing = (s: Pick<DeliveredSessionView, 'paymentStatus'>): boolean =>
  (OWING_STATUSES as readonly PaymentStatus[]).includes(s.paymentStatus);

/** Patient history: newest first, voided rows kept (faded by the caller). */
export function historyOrder<T extends { deliveredAt: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => Date.parse(b.deliveredAt) - Date.parse(a.deliveredAt));
}

/** The outstanding work queue: EFFECTIVE sessions with money still owed (unpaid or partial), oldest first. */
export function unpaidQueue<
  T extends { effective: boolean; paymentStatus: PaymentStatus; deliveredAt: string },
>(items: readonly T[]): T[] {
  return items
    .filter((s) => s.effective && isOwing(s))
    .sort((a, b) => Date.parse(a.deliveredAt) - Date.parse(b.deliveredAt));
}

/**
 * Legacy voided-but-paid rows (payment recorded before the patient-account
 * release): the money was never modelled, so it is the ONE state that still
 * needs a person's attention — rendered lightly where it exists, never built
 * into a workflow.
 */
export function financialAttentionRows<
  T extends { financialAttention?: 'voided_paid' | null; deliveredAt: string },
>(items: readonly T[]): T[] {
  return items
    .filter((s) => s.financialAttention === 'voided_paid')
    .sort((a, b) => Date.parse(b.deliveredAt) - Date.parse(a.deliveredAt));
}

/** Fully paid history: newest payment first; voided rows stay (their own chip). */
export function paidHistory<T extends { paymentStatus: PaymentStatus; paidAt: string | null }>(
  items: readonly T[],
): T[] {
  return items
    .filter((s) => s.paymentStatus === 'paid')
    .sort((a, b) => Date.parse(b.paidAt ?? '') - Date.parse(a.paidAt ?? ''));
}

/** A session as a payment surface may see it: operational facts only. */
export type PaymentRow<T extends DeliveredSessionView = DeliveredSessionView> = Omit<
  T,
  'notes' | 'outcomeNotes' | 'groupReport'
>;

/**
 * The reception boundary, applied regardless of what the API returned
 * (ZOY-264 closed without a payment DTO: the frontend simply never renders
 * clinical fields on a payment surface).
 */
export function stripClinical<T extends DeliveredSessionView>(s: T): PaymentRow<T> {
  const row = { ...s } as Record<string, unknown>;
  delete row.notes;
  delete row.outcomeNotes;
  // ZOY-686: the shared Group Report is clinical writing too.
  delete row.groupReport;
  return row as unknown as PaymentRow<T>;
}

/**
 * ZOY-651: a session read carries its staff names for every reader, so a
 * name is shown from the read itself; the personnel-based lookup (and its
 * "unnamed" placeholder) is only the fallback for an empty or absent name.
 */
export const staffNameOf = (
  name: string | null | undefined,
  uid: string | null | undefined,
  fallback: (uid: string | null | undefined) => string,
): string => (typeof name === 'string' && name.trim() !== '' ? name : fallback(uid));

export const methodLabelKey = (m: PaymentMethod | null): string =>
  m ? `py.method.${m}` : 'py.method.none';

/**
 * ZOY-364: what an EMPTY payments table is allowed to claim. The global
 * "everything is recorded" copy is true only when NO filter narrowed the
 * list; with filters active the honest statement is that the filters — not
 * the data — emptied it, upgraded to the exact hidden count when the
 * unfiltered unpaid total is known (and back to the true global copy when
 * that total is genuinely zero).
 */
export type PaymentsEmptyKind =
  'queue-empty' | 'history-empty' | 'filtered-unpaid-hidden' | 'filtered';

export function paymentsEmptyKind(args: {
  tab: 'unpaid' | 'history';
  /** Clinic-wide count of sessions still owing IGNORING the user's filters; null while unknown. */
  filtersActive: boolean;
  /** Clinic-wide unpaid count IGNORING the user's filters; null while unknown. */
  clinicUnpaidCount: number | null;
}): PaymentsEmptyKind {
  if (!args.filtersActive) return args.tab === 'unpaid' ? 'queue-empty' : 'history-empty';
  if (args.tab === 'unpaid') {
    if (args.clinicUnpaidCount === 0) return 'queue-empty';
    if (args.clinicUnpaidCount !== null) return 'filtered-unpaid-hidden';
  }
  return 'filtered';
}
